import React from "react";
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { motion } from "framer-motion";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const EMERALD = "#059669";
const EMERALD_LIGHT = "#10b981";

const CostsFilters = ({
  filters,
  filterOpen,
  categoryOptions,
  supplierOptions,
  handleFilterChange,
  resetFilters,
  toggleFilters,
  applyFilters,
  refreshData,
  exportCsv,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.5 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(EMERALD, 0.02)} 0%, ${alpha(EMERALD_LIGHT, 0.05)} 100%)`,
          border: `1px solid ${alpha(EMERALD, 0.1)}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h2" fontWeight={600}>
            Filtros
          </Typography>
          <Box>
            <Tooltip title="Filtros">
              <IconButton onClick={toggleFilters} sx={{ color: EMERALD }}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Recarregar">
              <IconButton onClick={refreshData} sx={{ color: EMERALD }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Baixar CSV">
              <IconButton onClick={exportCsv} sx={{ color: EMERALD }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={filterOpen}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Categoria"
                variant="outlined"
                size="small"
                value={filters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: alpha(EMERALD, 0.4) },
                    "&.Mui-focused fieldset": { borderColor: EMERALD },
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: EMERALD },
                }}
              >
                <MenuItem value="">Todas</MenuItem>
                {categoryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Fornecedor"
                variant="outlined"
                size="small"
                value={filters.supplier}
                onChange={(e) => handleFilterChange("supplier", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: alpha(EMERALD, 0.4) },
                    "&.Mui-focused fieldset": { borderColor: EMERALD },
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: EMERALD },
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                {supplierOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Inicio"
                value={filters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    variant: "outlined",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Fim"
                value={filters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    variant: "outlined",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Valor Minimo"
                variant="outlined"
                size="small"
                type="number"
                value={filters.minValue}
                onChange={(e) => handleFilterChange("minValue", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R$</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: alpha(EMERALD, 0.4) },
                    "&.Mui-focused fieldset": { borderColor: EMERALD },
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: EMERALD },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Valor Maximo"
                variant="outlined"
                size="small"
                type="number"
                value={filters.maxValue}
                onChange={(e) => handleFilterChange("maxValue", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R$</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: alpha(EMERALD, 0.4) },
                    "&.Mui-focused fieldset": { borderColor: EMERALD },
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: EMERALD },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={resetFilters}
                  startIcon={<CloseIcon />}
                  sx={{
                    mr: 1,
                    borderColor: alpha(EMERALD, 0.3),
                    color: EMERALD,
                    "&:hover": {
                      borderColor: EMERALD,
                      backgroundColor: alpha(EMERALD, 0.04),
                    },
                  }}
                >
                  Limpar
                </Button>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  startIcon={<FilterListIcon />}
                  sx={{
                    background: `linear-gradient(135deg, ${EMERALD} 0%, ${EMERALD_LIGHT} 100%)`,
                    boxShadow: `0 4px 12px ${alpha(EMERALD, 0.3)}`,
                    "&:hover": {
                      boxShadow: `0 6px 20px ${alpha(EMERALD, 0.4)}`,
                    },
                  }}
                >
                  Aplicar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>
    </motion.div>
  );
};

export default CostsFilters;
